import { format } from "date-fns";
import escapeHtml from "escape-html";
import { isObject, transform } from "lodash-es";
import { Text, Descendant } from "slate";
import { jsx } from "slate-hyperscript";
import {
  DOSE_GKg,
  DOSE_MgKg,
  DOSE_MugKg,
  TEXT_TAGS,
  ELEMENT_TAGS
} from "../constants";

export const resolveNumber = (
  value: number | string | null | undefined,
  positive = true,
  includeZero = false
) => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null || value === "") {
    return null;
  }

  const numValue = +value;
  const positiveValue = positive && numValue > 0;
  const positiveAndZero = positive && includeZero && numValue >= 0;

  if (positiveValue || positiveAndZero || !positive) {
    return +value;
  }
};

export const resolveString = (value: string | null | undefined) => {
  if (value === undefined) {
    return undefined;
  }
  return value || null;
};

export const resolveDate = (
  value: Date | null | undefined,
  dateFormat = "MM/dd/y"
) => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  return format(value, dateFormat);
};

export const getTimerValue = (timeInSeconds: number) => {
  const hours = `0${Math.floor((timeInSeconds / 3600) % 3600)}`.slice(-2);
  const minutes = `0${Math.floor((timeInSeconds / 60) % 60)}`.slice(-2);
  const seconds = `0${timeInSeconds % 60}`.slice(-2);

  return `${hours}:${minutes}:${seconds}`;
};

export const deepOmit = (source: any, keyToOmit: string) => {
  function omitFromObject(obj: any) {
    return transform(obj, (result: any, value, key) => {
      if (key === keyToOmit) {
        return;
      }
      result[key] = isObject(value) ? omitFromObject(value) : value;
    });
  }
  return omitFromObject({ ...source });
};

export const convertDoseToMg = (
  dose: number,
  doseUnit: typeof DOSE_GKg | typeof DOSE_MgKg | typeof DOSE_MugKg | string
) =>
  doseUnit === DOSE_GKg
    ? dose * 1000
    : doseUnit === DOSE_MugKg
    ? dose / 1000
    : dose;

export const getUserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export const serialize = (node: Descendant): string => {
  if (Text.isText(node)) {
    let string = escapeHtml(node.text);
    if (node.bold) {
      string = `<strong>${string}</strong>`;
    }
    if (node.italic) {
      string = `<em>${string}</em>`;
    }
    if (node.underline) {
      string = `<u>${string}</u>`;
    }
    return string;
  }

  const children = node.children.map((n: any) => serialize(n)).join("");

  switch (node.type) {
    case "paragraph":
      return `<p>${children}</p>`;
    case "numbered-list":
      return `<ol>${children}</ol>`;
    case "bulleted-list":
      return `<ul>${children}</ul>`;
    case "list-item":
      return `<li>${children}</li>`;
    default:
      return children;
  }
};

export const deserialize = (
  el: Node
): (Descendant | string | null)[] | Descendant | string | null => {
  if (el.nodeType === 3) {
    return el.textContent;
  }

  if (el.nodeType !== 1) {
    return null;
  }

  if (el.nodeName === "BR") {
    return "\n";
  }

  const { nodeName } = el;

  let children = Array.from(el.childNodes)
    .map(deserialize)
    .flat();

  if (children.length === 0) {
    children = [{ text: "" }];
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el);
    return jsx("element", attrs, children);
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el);
    return children.map(child => jsx("text", attrs, child));
  }

  return children;
};
